/**
 * /projects/:projectSlug/envs/:envSlug/secrets/:path — single secret detail.
 *
 * What renders depends on the secret's visibility tier:
 *
 *   - `open`   — current value is shown in plain text, no extra click.
 *   - `hidden` — RevealCard with click-to-reveal + 30s auto-hide.
 *   - `sealed` — no value card at all; plaintext is read by service tokens
 *                via the API. The detail page is purely metadata + history.
 *
 * Common to all three: an Add-new-version form, a version-history list,
 * and a Visibility card that lets users change the tier (downgrades
 * require entering a new value in the same step — Doppler's invariant).
 *
 * The path comes from the route splat (`secrets/*`) and is URL-encoded by
 * the upstream links (encodeURIComponent on the path). We decode once here
 * and pass the decoded form to every SDK call.
 */

import { PageHeader } from "@/components/PageHeader";
import { Select } from "@/components/Select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type SecretsList,
  type SetSecretVisibilityBody,
  type UpdateSecretBody,
  type VersionMeta,
  type Visibility,
  WardkeyAPIError,
  isDowngrade,
  useActiveOrg,
  useApiMutation,
  useApiQuery,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

const REVEAL_AUTO_HIDE_MS = 30_000;
const MAX_VALUE_BYTES = 64 * 1024;

const CONTENT_TYPES = [
  { value: "text/plain", label: "Plain text" },
  { value: "application/json", label: "JSON" },
  { value: "application/octet-stream", label: "Binary (octet-stream)" },
  { value: "application/x-pem-file", label: "PEM (cert / key)" },
] as const;

export function SecretDetailRoute() {
  const activeOrg = useActiveOrg();
  const params = useParams<{ projectSlug: string; envSlug: string; "*": string }>();
  const projectSlug = params.projectSlug ?? "";
  const envSlug = params.envSlug ?? "";
  const rawSplat = params["*"] ?? "";
  // react-router decodes simple :params but leaves splats as-is, so do it
  // explicitly here. Our paths only contain [A-Za-z0-9_-] + '/' so a stray
  // decode call on an already-decoded string is a no-op.
  const secretPath = safeDecode(rawSplat);

  if (!projectSlug || !envSlug || !secretPath) {
    return (
      <div className="mx-auto max-w-[700px]">
        <div className="fade-up rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
          <p className="text-[14px] text-muted-foreground">
            Missing project, environment, or path in the URL.{" "}
            <Link to="/projects" className="text-foreground underline-offset-4 hover:underline">
              Back to projects
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <DetailView
      orgId={activeOrg.org_id}
      project={projectSlug}
      env={envSlug}
      secretPath={secretPath}
    />
  );
}

function DetailView({
  orgId,
  project,
  env,
  secretPath,
}: {
  orgId: string;
  project: string;
  env: string;
  secretPath: string;
}) {
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const versions = useApiQuery(
    ["secret-versions", orgId, project, env, secretPath],
    (c) => c.secrets.listVersions(orgId, project, env, secretPath),
    { suppressToast: true },
  );

  // Visibility isn't returned by the versions endpoint, so we piggy-back on
  // the list endpoint (which already returns `visibility` for every row).
  // The query gets cached, so navigating back to the list is free.
  const list = useApiQuery<SecretsList>(
    ["secrets", orgId, project, env],
    (c) => c.secrets.list(orgId, project, env),
    { suppressToast: true },
  );
  const listed = list.data?.secrets.find((s) => s.path === secretPath);
  const tier: Visibility | undefined = listed?.visibility;

  const remove = useApiMutation<void, void>(
    (c) => c.secrets.remove(orgId, project, env, secretPath),
    {
      suppressToast: true,
      onSuccess: () => {
        pushToast({ kind: "success", title: `Deleted ${secretPath}` });
        navigate(
          `/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets`,
        );
      },
      onError: (err) => {
        setConfirmingDelete(false);
        pushToast({
          kind: "error",
          title: `Couldn't delete ${secretPath}`,
          body: err instanceof WardkeyAPIError ? err.message : err.message,
        });
      },
    },
  );

  if (versions.isLoading) return <DetailSkeleton path={secretPath} />;
  if (versions.error) {
    return (
      <ScopedErrorState
        error={versions.error}
        backHref={`/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets`}
      />
    );
  }

  const versionList = versions.data?.versions ?? [];
  const current = versionList[0];

  return (
    <div className="secret-detail-layout">
      <Header
        secretPath={secretPath}
        current={current}
        tier={tier}
        versionCount={versionList.length}
        deleting={remove.isPending}
        confirming={confirmingDelete}
        onAskDelete={() => setConfirmingDelete(true)}
        onConfirmDelete={() => remove.mutate()}
        onCancelDelete={() => setConfirmingDelete(false)}
      />

      {(tier === "open" || tier === "hidden") && (
        <section className="secret-value-panel fade-up">
          <ValueCard
            orgId={orgId}
            project={project}
            env={env}
            secretPath={secretPath}
            current={current}
            tier={tier}
          />
        </section>
      )}

      <section className="secret-write-panel fade-up">
        <NewVersionCard
          orgId={orgId}
          project={project}
          env={env}
          secretPath={secretPath}
          onCreated={() => {
            versions.refetch();
          }}
        />
      </section>

      {tier && (
        <section className="secret-visibility-panel fade-up">
          <VisibilityCard
            orgId={orgId}
            project={project}
            env={env}
            secretPath={secretPath}
            currentTier={tier}
            onChanged={() => {
              versions.refetch();
              list.refetch();
            }}
          />
        </section>
      )}

      <section className="secret-history-panel fade-up">
        <VersionHistory versions={versionList} />
      </section>
    </div>
  );
}

function Header({
  secretPath,
  current,
  tier,
  versionCount,
  deleting,
  confirming,
  onAskDelete,
  onConfirmDelete,
  onCancelDelete,
}: {
  secretPath: string;
  current: VersionMeta | undefined;
  tier: Visibility | undefined;
  versionCount: number;
  deleting: boolean;
  confirming: boolean;
  onAskDelete: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}) {
  const subtitle = current ? (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[12px] text-muted-foreground">
      <span>{`current v${current.version}`}</span>
      <span aria-hidden>·</span>
      <span title={new Date(current.created_at * 1000).toLocaleString()}>
        {`updated ${formatRelative(current.created_at)}`}
      </span>
      <span aria-hidden>·</span>
      <span>{`${versionCount} version${versionCount === 1 ? "" : "s"}`}</span>
    </span>
  ) : (
    <span className="font-mono text-[12px] text-muted-foreground">no versions</span>
  );

  return (
    <div className="fade-up">
      <PageHeader
        title={<span className="break-all font-mono">{secretPath}</span>}
        adornment={tier && <VisibilityChip tier={tier} />}
        subtitle={subtitle}
        actions={
          confirming ? (
            <>
              <span className="font-mono text-[10px] uppercase tracking-widest text-destructive">
                delete this secret?
              </span>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onConfirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Confirm delete"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancelDelete}
                disabled={deleting}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={onAskDelete}>
              Delete
            </Button>
          )
        }
      />
    </div>
  );
}

/**
 * Tier-aware value card. For `open` secrets the plaintext is fetched and
 * displayed automatically on mount. For `hidden`, the value is loaded
 * lazily on Reveal click and dotted out until Show, with a 30s auto-hide
 * after the user reveals it. Never rendered for `sealed`.
 */
function ValueCard({
  orgId,
  project,
  env,
  secretPath,
  current,
  tier,
}: {
  orgId: string;
  project: string;
  env: string;
  secretPath: string;
  current: VersionMeta | undefined;
  tier: "open" | "hidden";
}) {
  const { pushToast } = useToast();
  const [shown, setShown] = useState(tier === "open");
  const [revealed, setRevealed] = useState<string | null>(null);

  const reveal = useApiMutation<void, { value: string; version: number }>(
    (c) => c.secrets.readValue(orgId, project, env, secretPath),
    {
      onSuccess: (v) => {
        setRevealed(v.value);
        setShown(true);
      },
    },
  );

  // `open` tier auto-fetches on mount — no Reveal click needed. Intentionally
  // narrow deps: only re-fire when the tier or active secret swaps; the
  // mutation object and `revealed` are stable enough for this guard.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (tier === "open" && revealed === null && !reveal.isPending && current) {
      reveal.mutate();
    }
  }, [tier, current?.version]);

  // `hidden` tier auto-hides after a short window once revealed.
  useEffect(() => {
    if (tier !== "hidden" || !shown) return;
    const t = setTimeout(() => {
      setShown(false);
      setRevealed(null);
    }, REVEAL_AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [tier, shown]);

  async function copyValue() {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      pushToast({ kind: "success", title: "Copied to clipboard" });
    } catch {
      pushToast({ kind: "error", title: "Clipboard copy failed" });
    }
  }

  const description =
    tier === "open"
      ? "This secret is marked open — the value is shown without an extra click."
      : "Plaintext is fetched on demand and dotted out by default. Reveals are audit-logged.";

  return (
    <Card
      eyebrow="current value"
      title={current ? `v${current.version}` : "—"}
      description={description}
    >
      <div className="rounded-md border border-border bg-muted p-3">
        {revealed ? (
          <div
            data-testid="current-value"
            className={`break-all font-mono text-[13px] ${shown ? "text-foreground" : "select-none text-muted-foreground/70"}`}
          >
            {shown ? revealed : "•".repeat(Math.min(40, Math.max(8, revealed.length)))}
          </div>
        ) : (
          <div className="select-none font-mono text-[13px] text-muted-foreground/70">
            {"•".repeat(32)}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!revealed ? (
            <Button
              type="button"
              size="sm"
              onClick={() => reveal.mutate()}
              disabled={reveal.isPending || !current}
            >
              {reveal.isPending ? "Revealing…" : "Reveal"}
            </Button>
          ) : (
            <>
              {tier === "hidden" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShown((v) => !v)}
                >
                  {shown ? "Hide" : "Show"}
                </Button>
              )}
              <Button type="button" size="sm" onClick={copyValue}>
                Copy
              </Button>
            </>
          )}
        </div>
        {tier === "hidden" && shown && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
            auto-hides in {Math.round(REVEAL_AUTO_HIDE_MS / 1000)}s · access audit-logged
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * Visibility-change card. Upgrades (open → hidden → sealed) commit
 * directly; downgrades require entering a new value in the same submit
 * so the relaxation can't widen access to the prior plaintext.
 */
function VisibilityCard({
  orgId,
  project,
  env,
  secretPath,
  currentTier,
  onChanged,
}: {
  orgId: string;
  project: string;
  env: string;
  secretPath: string;
  currentTier: Visibility;
  onChanged: () => void;
}) {
  const { pushToast } = useToast();
  const [pending, setPending] = useState<Visibility | null>(null);
  const [downgradeValue, setDowngradeValue] = useState("");
  const [downgradeCt, setDowngradeCt] = useState("text/plain");
  const [error, setError] = useState<string | null>(null);

  const tiers: Visibility[] = ["open", "hidden", "sealed"];
  const target = pending ?? currentTier;
  const downgrading = target !== currentTier && isDowngrade(currentTier, target);
  const noChange = target === currentTier;

  const change = useApiMutation<SetSecretVisibilityBody, unknown>(
    (c, body) => c.secrets.setVisibility(orgId, project, env, secretPath, body),
    {
      suppressToast: true,
      onSuccess: () => {
        if (!pending) return;
        pushToast({ kind: "success", title: `Visibility set to ${pending}` });
        setPending(null);
        setDowngradeValue("");
        setDowngradeCt("text/plain");
        setError(null);
        onChanged();
      },
      onError: (e) => {
        const msg = e instanceof WardkeyAPIError ? e.message : e.message;
        setError(msg);
        pushToast({ kind: "error", title: "Couldn't change visibility", body: msg });
      },
    },
  );

  function commit() {
    if (!pending || pending === currentTier) return;
    if (downgrading) {
      if (downgradeValue.length === 0) {
        setError("New value is required when relaxing visibility.");
        return;
      }
      const bytes = new TextEncoder().encode(downgradeValue).byteLength;
      if (bytes > MAX_VALUE_BYTES) {
        setError(`Value is ${formatBytes(bytes)} — max ${formatBytes(MAX_VALUE_BYTES)}.`);
        return;
      }
    }
    setError(null);
    change.mutate({
      visibility: pending,
      ...(downgrading ? { value: downgradeValue, content_type: downgradeCt } : {}),
    });
  }

  const submitting = change.isPending;

  return (
    <Card
      eyebrow="visibility"
      title="Change visibility tier"
      description="Lowering visibility (sealed → hidden → open) requires a new value in the same step — Wardkey rotates the secret atomically so the old bytes never leak to the wider audience."
    >
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Visibility tier">
        {tiers.map((tier) => {
          const selected = target === tier;
          const isCurrent = tier === currentTier;
          return (
            <button
              key={tier}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: custom-styled radio with description block — native <input type="radio"> can't host the layout
              role="radio"
              aria-checked={selected}
              onClick={() => setPending(tier === currentTier ? null : tier)}
              className={`rounded-md border px-3 py-3 text-left transition-colors ${
                selected
                  ? "border-foreground/30 bg-muted"
                  : "border-border bg-card hover:bg-muted/50"
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span
                  className={`font-mono text-[11px] uppercase tracking-widest ${
                    selected ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {tier}
                </span>
                {isCurrent && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    current
                  </span>
                )}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-muted-foreground">
                {tier === "open"
                  ? "value shown automatically"
                  : tier === "hidden"
                    ? "click-to-reveal in console"
                    : "service tokens only"}
              </div>
            </button>
          );
        })}
      </div>

      {downgrading && (
        <div className="mt-5 space-y-3 rounded-md border border-yellow-500/30 bg-yellow-500/5 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300">
            new value required
          </div>
          <p className="text-[13px] text-muted-foreground">
            Relaxing visibility from <code className="font-mono">{currentTier}</code> to{" "}
            <code className="font-mono">{target}</code> rotates the secret. Provide the value the
            new tier should hold.
          </p>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              new value
            </span>
            <Textarea
              value={downgradeValue}
              onChange={(e) => setDowngradeValue(e.target.value)}
              rows={3}
              spellCheck={false}
              className="mt-2 font-mono resize-y"
            />
          </label>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              content type
            </span>
            <div className="mt-2">
              <Select
                options={CONTENT_TYPES}
                value={downgradeCt}
                onChange={setDowngradeCt}
                ariaLabel="Content type"
              />
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 font-mono text-[11px] text-destructive" role="alert">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        {pending && pending !== currentTier && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setPending(null);
              setError(null);
              setDowngradeValue("");
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
        <Button type="button" size="sm" onClick={commit} disabled={submitting || noChange}>
          {submitting ? "Saving…" : noChange ? "No change" : `Set to ${target}`}
        </Button>
      </div>
    </Card>
  );
}

function VisibilityChip({ tier }: { tier: Visibility }) {
  const toneClass =
    tier === "sealed"
      ? "border-border bg-muted text-foreground"
      : tier === "open"
        ? "border-border bg-transparent text-muted-foreground/70"
        : "border-border bg-transparent text-muted-foreground";
  return (
    <span
      className={`shrink-0 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${toneClass}`}
      aria-label={`visibility: ${tier}`}
    >
      {tier}
    </span>
  );
}

function NewVersionCard({
  orgId,
  project,
  env,
  secretPath,
  onCreated,
}: {
  orgId: string;
  project: string;
  env: string;
  secretPath: string;
  onCreated: () => void;
}) {
  const { pushToast } = useToast();
  const [value, setValue] = useState("");
  const [contentType, setContentType] = useState("text/plain");
  const [touched, setTouched] = useState(false);

  const update = useApiMutation<UpdateSecretBody, unknown>(
    (c, body) => c.secrets.update(orgId, project, env, secretPath, body),
    {
      suppressToast: true,
      onSuccess: () => {
        setValue("");
        setTouched(false);
        pushToast({ kind: "success", title: "New version stored" });
        onCreated();
      },
      onError: (err) => {
        pushToast({
          kind: "error",
          title: "Couldn't store new version",
          body: err instanceof WardkeyAPIError ? err.message : err.message,
        });
      },
    },
  );

  const error = touched ? validateValue(value) : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (validateValue(value) !== null) return;
    update.mutate({ value, content_type: contentType });
  }

  return (
    <Card
      eyebrow="add new version"
      title="Rotate value"
      description="Creates a new version. Older versions stay readable from the history list below — Wardkey never destroys past values."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            value
          </span>
          <Textarea
            aria-label="New version value"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setTouched(true);
            }}
            placeholder="paste or type"
            rows={4}
            spellCheck={false}
            aria-invalid={error !== null}
            className="mt-2 font-mono resize-y"
          />
          {error && (
            <p className="mt-2 font-mono text-[11px] text-destructive" role="alert">
              {error}
            </p>
          )}
        </label>

        <div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            content type
          </span>
          <div className="mt-2">
            <Select
              options={CONTENT_TYPES}
              value={contentType}
              onChange={setContentType}
              ariaLabel="Content type"
            />
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={update.isPending || validateValue(value) !== null}
          >
            {update.isPending ? "Encrypting…" : "Store new version"}
            <span aria-hidden>→</span>
          </Button>
        </div>
      </form>
    </Card>
  );
}

function VersionHistory({ versions }: { versions: ReadonlyArray<VersionMeta> }) {
  if (versions.length === 0) {
    return (
      <Card eyebrow="history" title="No versions" description="Stored versions will appear here.">
        <p className="text-[13px] text-muted-foreground">Nothing yet.</p>
      </Card>
    );
  }

  return (
    <Card
      eyebrow="history"
      title="Version history"
      description="Every version is encrypted with the tenant KEK in force at write time. Plaintext is read by your apps via the API — not from the console."
    >
      <ul className="divide-y divide-border">
        {versions.map((v) => (
          <VersionRow key={v.version} version={v} isCurrent={v === versions[0]} />
        ))}
      </ul>
    </Card>
  );
}

function VersionRow({ version, isCurrent }: { version: VersionMeta; isCurrent: boolean }) {
  return (
    <li className="flex items-center gap-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[13px] text-foreground">v{version.version}</span>
          {isCurrent && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              current
            </span>
          )}
        </div>
        <div
          className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70"
          title={new Date(version.created_at * 1000).toLocaleString()}
        >
          created {formatRelative(version.created_at)} · kek v{version.kek_version}
        </div>
      </div>
    </li>
  );
}

function Card({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
          {eyebrow}
        </div>
        <h2 className="mt-1 font-sans text-base font-semibold tracking-tight text-foreground">
          {title}
        </h2>
        {description && (
          <p className="mt-1.5 max-w-[40rem] text-[13px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function DetailSkeleton({ path }: { path: string }) {
  return (
    <div className="mx-auto max-w-[820px]">
      <div className="fade-up mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-border pb-5">
        <div className="min-w-0">
          <h1 className="truncate break-all font-mono text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            {path}
          </h1>
          <Skeleton className="mt-2 block h-3 w-40 rounded-sm" />
        </div>
        <Skeleton className="h-8 w-20" />
      </div>
      <div className="mt-6 grid gap-6">
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-44 rounded-lg" />
      </div>
    </div>
  );
}

function ScopedErrorState({ error, backHref }: { error: Error; backHref: string }) {
  const isNotFound = error instanceof WardkeyAPIError && error.status === 404;
  return (
    <div className="mx-auto max-w-[700px]">
      <div className="fade-up rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {isNotFound ? "not found" : "load failed"}
        </div>
        <h2 className="mt-3 text-xl font-medium text-foreground">
          {isNotFound ? "This secret doesn't exist" : "Couldn't load this secret"}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-[14px] leading-relaxed text-muted-foreground">
          {isNotFound
            ? "It may have been deleted, or the path in the URL is wrong."
            : error instanceof WardkeyAPIError
              ? error.message
              : error.message || "Request failed"}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={backHref}>Back to secrets</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

function validateValue(value: string): string | null {
  if (value.length === 0) return "Value is required.";
  const bytes = new TextEncoder().encode(value).byteLength;
  if (bytes > MAX_VALUE_BYTES) {
    return `Value is ${formatBytes(bytes)} — max ${formatBytes(MAX_VALUE_BYTES)}.`;
  }
  return null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(epochSeconds: number): string {
  const deltaSec = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (deltaSec < 60) return "just now";
  const m = Math.floor(deltaSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}
